import type { Board, GeometryElement } from 'jsxgraph';
import type { JsxgraphThemeService } from '../jsxgraph-theme.service';
import type { ActionDefinition } from '../../../model/widget-action.model';

/** Configuration for a single canvas selection element */
/** Concrete location or expression with string references (ports) */
export interface CanvasSelectionConfig {
  id: string;
  x: number | (() => number);
  y: number | (() => number);
  label: string;
  trigger: ActionDefinition;
  triggerOff?: ActionDefinition;
  group?: string;
  selected?: boolean;
}

/** Options for binding canvas selections to a board */
export interface BindCanvasSelectionsOptions {
  board: Board;
  themeService: JsxgraphThemeService;
  configs: CanvasSelectionConfig[];
  onSelect: (config: CanvasSelectionConfig, selected: boolean) => Promise<void>;
}

/** Result of binding canvas selections */
export interface BoundCanvasSelections {
  /** Get the selected element ID for a group (undefined if none selected) */
  getSelectedId(group: string): string | undefined;
  /** Check if a standalone (ungrouped) element is selected */
  isSelected(id: string): boolean;
  /** Programmatically select an element (updates visuals only, does NOT trigger action) */
  selectById(id: string): void;
  /** All JSXGraph elements created */
  getAllJsxObjects(): GeometryElement[];
  destroy(): void;
}

// Visual constants (fixed pixel size)
const OUTER_SIZE = 20;
const INNER_SIZE = 8;
const STROKE_WIDTH = 2;

function createSelectionHtml(outerStroke: string, outerFill: string, innerFill: string): string {
  return `<div style="
    width: ${OUTER_SIZE}px;
    height: ${OUTER_SIZE}px;
    border-radius: 50%;
    border: ${STROKE_WIDTH}px solid ${outerStroke};
    background: ${outerFill};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-sizing: border-box;
  "><div style="
    width: ${INNER_SIZE}px;
    height: ${INNER_SIZE}px;
    border-radius: 50%;
    background: ${innerFill};
  "></div></div>`;
}

/**
 * Bind canvas selection controls to a JSXGraph board.
 * Creates clickable circle elements at board coordinates with radio-button or toggle behavior.
 */
export function bindCanvasSelections(options: BindCanvasSelectionsOptions): BoundCanvasSelections {
  const { board, themeService, configs, onSelect } = options;

  if (configs.length === 0) {
    return {
      getSelectedId: () => undefined,
      isSelected: () => false,
      selectById: () => {
        /* noop */
      },
      getAllJsxObjects: () => [],
      destroy: () => {
        /* noop */
      },
    };
  }

  // Resolve theme colors
  const neutral300 = themeService.resolveColorToken('neutral-300');
  const sky100 = themeService.resolveColorToken('sky-100');
  const sky500 = themeService.resolveColorToken('sky-500');

  // State tracking
  const selectedByGroup = new Map<string, string>(); // group -> selected id
  const standaloneSelected = new Set<string>(); // ungrouped selected ids
  const selectionState = new Map<string, boolean>(); // id -> selected

  // JSXGraph elements for cleanup
  const jsxObjects: GeometryElement[] = [];
  const cleanupFns: Array<() => void> = [];

  // Initialize selection state.
  // For grouped controls, only the first `selected: true` wins (radio semantics).
  for (const config of configs) {
    const wantsSelected = config.selected ?? false;

    if (config.group) {
      const groupAlreadyHasSelection = selectedByGroup.has(config.group);
      const isSelected = wantsSelected && !groupAlreadyHasSelection;
      selectionState.set(config.id, isSelected);
      if (isSelected) {
        selectedByGroup.set(config.group, config.id);
      }
    } else {
      selectionState.set(config.id, wantsSelected);
      if (wantsSelected) {
        standaloneSelected.add(config.id);
      }
    }
  }

  // Create elements
  for (const config of configs) {
    const htmlFn = () => {
      const selected = selectionState.get(config.id) ?? false;
      const stroke = selected ? sky500 : neutral300;
      const fill = selected ? sky100 : 'transparent';
      return createSelectionHtml(stroke, fill, sky500);
    };

    const textEl = board.create('text', [config.x, config.y, htmlFn], {
      anchorX: 'middle',
      anchorY: 'middle',
      parse: false,
      useMathjax: false,
      useMathJax: false,
      highlight: false,
      fixed: true,
      layer: 9,
    }) as GeometryElement;

    jsxObjects.push(textEl);

    // Attach click handler via rendNode.
    // Ensure pointer events are enabled and element sits above JSXGraph's event layer.
    const rendNode = (textEl as unknown as { rendNode: HTMLElement }).rendNode;
    if (rendNode) {
      rendNode.style.pointerEvents = 'auto';
      rendNode.style.zIndex = '100';
      rendNode.style.cursor = 'pointer';

      const onClick = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (config.group) {
          // Radio-button: skip if already selected
          const currentSelected = selectedByGroup.get(config.group);
          if (currentSelected === config.id) {
            return;
          }

          // Deselect current
          if (currentSelected) {
            selectionState.set(currentSelected, false);
          }

          // Select this one
          selectionState.set(config.id, true);
          selectedByGroup.set(config.group, config.id);
        } else {
          // Toggle
          const current = selectionState.get(config.id) ?? false;
          const next = !current;
          selectionState.set(config.id, next);
          if (next) {
            standaloneSelected.add(config.id);
          } else {
            standaloneSelected.delete(config.id);
          }
        }

        board.update();
        onSelect(config, selectionState.get(config.id) ?? false);
      };

      rendNode.addEventListener('click', onClick);
      cleanupFns.push(() => rendNode.removeEventListener('click', onClick));
    }
  }

  // Fire initial triggers for pre-selected controls
  for (const config of configs) {
    if (selectionState.get(config.id)) {
      onSelect(config, true);
    }
  }

  return {
    getSelectedId(group: string): string | undefined {
      return selectedByGroup.get(group);
    },

    isSelected(id: string): boolean {
      return selectionState.get(id) ?? false;
    },

    selectById(id: string): void {
      const config = configs.find((c) => c.id === id);
      if (!config) {
        return;
      }

      if (config.group) {
        const currentSelected = selectedByGroup.get(config.group);
        if (currentSelected) {
          selectionState.set(currentSelected, false);
        }
        selectionState.set(id, true);
        selectedByGroup.set(config.group, id);
      } else {
        selectionState.set(id, true);
        standaloneSelected.add(id);
      }

      board.update();
    },

    getAllJsxObjects(): GeometryElement[] {
      return jsxObjects;
    },

    destroy(): void {
      for (const fn of cleanupFns) {
        fn();
      }
      cleanupFns.length = 0;

      for (const obj of jsxObjects) {
        board.removeObject(obj);
      }
      jsxObjects.length = 0;

      selectionState.clear();
      selectedByGroup.clear();
      standaloneSelected.clear();
    },
  };
}
